import React, { useState } from 'react';
import { string, arrayOf, bool, func } from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { InlineTextButton, AppointmentMessage, TransitionMessage } from '../../components';
import { ensureTransaction, ensureUser, ensureListing } from '../../util/data';
import {
    handleOtherPartyForDeletedListing,
    isRelevantPastTransition,
    resolveOtherPartyData,
    TRANSITION_ENQUIRY_DECLINE,
    TRANSITION_ENQUIRY_DECLINE_OWNER,
    TRANSITION_ENQUIRY_EXPIRE,
} from '../../util/transaction';
import { anonymizeOtherParty } from '../../util/anonym';
import { propTypes } from '../../util/types';
import css from './ActivityFeed.css';
import {
    chatItemFabric,
    isOwnMessage,
    resolveChatLastActivity,
    resolveMessageDateGroups,
} from './ActivityFeed.helpers';
import EmptyTransition from './components/EmptyTransition';
import Message from './components/Message';
import MessageStatus from './components/MessageStatus';
import ReinitiateTransactionButton from './components/ReinitiateTransactionButton';
import NotificationAvailability from './components/NotificationAvailability';
import NotificationListingState from './components/NotificationListingState';
import ContactPreferenceModal from '../../forms/ContactDetailsForm/ContactPreferenceModal';
import { useTransactionPanelContext } from '../TransactionPanel/TransactionPanelContext';

export const ActivityFeedComponent = ({
    rootClassName,
    className,
    transaction,
    currentUser,
    hasOlderMessages,
    onShowOlderMessages,
    fetchMessagesInProgress,
    intl,
    handleImageCarouselVisibility,
    ...rest
}) => {
    const { organizedItems } = useTransactionPanelContext();
    const currentTransaction = ensureTransaction(transaction);
    const currentCustomer = ensureUser(currentTransaction.customer);
    const currentProvider = ensureUser(currentTransaction.provider);
    const currentListing = ensureListing(currentTransaction.listing);
    const transactionId = currentTransaction.id.uuid;
    const txSharedContactInfo = useSelector(s => s['TransactionPage'].txSharedContactInfo);
    const fetchTxSharedContactInfoInProgress = useSelector(
        s => s['TransactionPage'].fetchTxSharedContactInfoInProgress
    );
    const fetchTxSharedContactInfoError = useSelector(
        s => s['TransactionPage'].fetchTxSharedContactInfoError
    );
    const currentTxSharedContactInfo = (txSharedContactInfo || {})[transactionId];
    const lastVisit = resolveChatLastActivity(currentTransaction, currentUser, currentListing);

    const [isContactPreferenceModalOpen, setIsContactPreferenceModalOpen] = useState(false);

    const transitionsAvailable = !!(
        currentUser &&
        currentUser.id &&
        currentCustomer.id &&
        currentProvider.id
    );

    const { lastTransition } = currentTransaction.attributes;

    const otherParty = handleOtherPartyForDeletedListing(
        anonymizeOtherParty(
            resolveOtherPartyData(currentUser, currentCustomer, currentProvider),
            currentUser,
            currentListing
        ),
        currentListing
    );

    const lastListItem = (organizedItems || [])[organizedItems.length - 1];
    const lastListItemIsTransition = lastListItem && lastListItem.transition;

    const dateGroups = resolveMessageDateGroups(organizedItems, intl).sort((a, b) => {
        if (b.index > 1 && a.index > 1) {
            return a.index - b.index;
        }
        return b.index - a.index;
    });

    const transitionComponent = transition =>
        isRelevantPastTransition(transition.transition) ? (
            <li>
                {transitionsAvailable ? (
                    <TransitionMessage
                        transition={transition}
                        transaction={transaction}
                        currentUser={currentUser}
                        currentListing={currentListing}
                        intl={intl}
                        /**
                         * TODO check the prop
                         */
                        isCustomer={false}
                        {...rest}
                    />
                ) : (
                    <EmptyTransition />
                )}
            </li>
        ) : null;

    const appointmentComponent = message => {
        const { createdAt } = message;
        const messageIsRead = lastVisit ? new Date(lastVisit) > new Date(createdAt) : false;

        return (
            <li key={message._id} className={css.messageItem}>
                <AppointmentMessage
                    currentCustomer={currentCustomer}
                    currentProvider={currentProvider}
                    currentUser={currentUser}
                    currentListing={currentListing}
                    appointmentData={message}
                    {...rest}
                >
                    {isOwnMessage(message.sender.userId, currentUser) && (
                        <MessageStatus messageIsViewedByOtherParty={messageIsRead} />
                    )}
                </AppointmentMessage>
            </li>
        );
    };

    const messageComponent = (message, extraProps = {}) => (
        <li key={message.id.uuid} className={css.messageItem}>
            <Message
                message={message}
                currentTransaction={transaction}
                isOwnMessage={isOwnMessage(message.sender.id.uuid, currentUser)}
                handleImageCarouselVisibility={handleImageCarouselVisibility}
                otherParty={otherParty}
                messageIsViewedByOtherParty={
                    lastVisit ? new Date(lastVisit) > new Date(message.attributes.createdAt) : false
                }
                {...extraProps}
            />
        </li>
    );

    const classes = classNames(rootClassName || css.feed, {
        [className]: !!className,
        [css.lastListItemIsTransition]: !!lastListItemIsTransition,
    });

    const currentUserIsProvider = currentUser?.id?.uuid === currentProvider?.id?.uuid;

    const chatIsExpired = lastTransition === TRANSITION_ENQUIRY_EXPIRE;
    const chatIsDeclined =
        lastTransition === TRANSITION_ENQUIRY_DECLINE ||
        lastTransition === TRANSITION_ENQUIRY_DECLINE_OWNER;

    const showReinitiateButton = chatIsExpired || (chatIsDeclined && currentUserIsProvider);

    return (
        <ul className={classes}>
            {hasOlderMessages ? (
                <li className={css.showOlderWrapper} key="show-older-messages">
                    <InlineTextButton className={css.showOlderButton} onClick={onShowOlderMessages}>
                        <span>Ältere anzeigen</span>
                    </InlineTextButton>
                </li>
            ) : null}
            {dateGroups.map(
                (dateGroup, index) =>
                    !!dateGroup.items.length && (
                        <li key={index}>
                            <h4 className={css.messageGroup}>{dateGroup.group}</h4>
                            <ul>
                                {dateGroup.items.map(item =>
                                    chatItemFabric({
                                        item,
                                        transitionComponent,
                                        messageComponent,
                                        appointmentComponent,
                                        shareContactInfoComponent: message =>
                                            messageComponent(message, {
                                                isContactInfoSharing: true,
                                                txSharedContactInfo: currentTxSharedContactInfo,
                                                fetchTxSharedContactInfoInProgress,
                                                fetchTxSharedContactInfoError,
                                                openContactPreferenceModal: () =>
                                                    setIsContactPreferenceModalOpen(true),
                                            }),
                                    })
                                )}
                            </ul>
                        </li>
                    )
            )}
            <NotificationAvailability currentUser={currentUser} otherParty={otherParty} />
            <NotificationListingState
                currentUser={currentUser}
                currentListing={currentListing}
                otherParty={otherParty}
                currentTransaction={currentTransaction}
            />
            {showReinitiateButton && (
                <ReinitiateTransactionButton
                    transaction={currentTransaction}
                    listing={currentListing}
                    rootClassName={css.reinitiateTxButton}
                />
            )}
            {isContactPreferenceModalOpen && (
                <ContactPreferenceModal
                    isOpen
                    onClose={() => setIsContactPreferenceModalOpen(false)}
                />
            )}
        </ul>
    );
};

ActivityFeedComponent.propTypes = {
    rootClassName: string,
    className: string,

    currentUser: propTypes.currentUser,
    transaction: propTypes.transaction,
    messages: arrayOf(propTypes.message),
    hasOlderMessages: bool.isRequired,
    // onOpenReviewModal: func.isRequired,
    onShowOlderMessages: func,
    fetchMessagesInProgress: bool.isRequired,
    // from injectIntl
    intl: intlShape.isRequired,
};

const ActivityFeed = injectIntl(ActivityFeedComponent);

export default ActivityFeed;
